import { Link } from 'react-router-dom';
import { HOME } from '@/constants/testIds';
import { TRADES, CALCULATORS } from '@/lib/calculators';
import { ArrowRight, Hammer, Calculator, Bot, FileText, ShieldCheck } from 'lucide-react';
import { TrustpilotWidget } from '@/components/Trustpilot';
import Seo from '@/components/Seo';

const PILLS = ['Concrete m³', 'Bricks/m²', 'BTU Rads', 'Voltage drop', 'Tile area', 'Paint litres', 'Joist span', 'Skip size', 'Felt rolls', 'Worktop runs', 'Plaster bags', 'Studs @400'];

const LANDING_FAQ = [
  { q: 'Is TradesMate free to use?', a: 'Yes — all 95+ trade calculators, the guides and the live fuel-price tracker are free to use with no sign-up. A Pro subscription (14-day free trial) unlocks saved quotes, the AI estimator, branded PDF invoices and job profit tracking.' },
  { q: 'What calculators does TradesMate include?', a: 'Every material and estimating calculator a UK tradesman needs — bricks and blocks per m², concrete volume, mortar mixes, paving, plaster, paint coverage, tiles, roofing, timber, pipe sizing, BTU/boiler sizing, cable sizing to BS 7671 and much more, grouped by trade.' },
  { q: 'Can I create professional quotes and invoices?', a: 'Yes. TradesMate turns your calculations into branded, professional quotes and invoices with labour, materials, VAT and margin, ready to send as a PDF or a shareable link your customer can accept online.' },
  { q: 'Is there a free fuel price checker?', a: 'Yes — the built-in fuel tracker shows live petrol and diesel prices from major UK retailers near any postcode, plus nearby EV charging points. It\u2019s free and needs no account.' },
  { q: 'Do I need to be a tradesman to use it?', a: 'No. Anyone can use the free calculators, guides and fuel tracker. The quoting, job tracking and business tools are built for tradesmen and small trade businesses.' },
];

const LANDING_JSONLD = [
  {
    '@context': 'https://schema.org', '@type': 'SoftwareApplication',
    name: 'TradesMate UK', applicationCategory: 'BusinessApplication', operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
    description: 'Free UK trade calculators and estimating software for tradesmen — 95+ material calculators, quotes, branded invoices and job profit tracking.',
  },
  {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: LANDING_FAQ.map((f) => ({ '@type': 'Question', name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

export default function Landing() {
  return (
    <div className="bg-[#F7F5F0]">
      <Seo
        title="Trade Calculators & Estimating for UK Tradesmen"
        description="Free UK trade calculators and estimating software. 40+ material & quantity calculators, professional quotes, branded PDF invoices and a live job profit tracker — built for UK tradesmen."
        path="/"
        jsonLd={LANDING_JSONLD}
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute -top-40 -right-40 w-[38rem] h-[38rem] rounded-full bg-brand/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 tm-rise">
            <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold mb-6" data-testid="hero-eyebrow">
              <span className="h-px w-10 bg-gold" /> Estimating &amp; business software · UK
            </p>
            <h1 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900">
              Every calculator a UK tradesman <em className="italic text-brand">actually</em> needs.
            </h1>
            <p className="mt-5 font-display italic text-lg text-slate-600" data-testid="hero-slogan">
              Built by a tradesman, for tradesmen.
            </p>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              Materials, mortar, voltage drop, BTU loads, paint coverage, full job estimates with labour, VAT &amp; margin. Built for the van, the site and the kitchen table.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/register" data-testid={HOME.heroCta} className="tm-btn">
                Start estimating <ArrowRight size={16} />
              </Link>
              <Link to="/trades" data-testid="hero-cta-browse" className="tm-btn tm-btn-ghost">Browse calculators</Link>
              <Link to="/login" data-testid={HOME.heroLogin} className="tm-btn tm-btn-dark">Log in</Link>
            </div>
            <div className="mt-12 grid grid-cols-3 max-w-md divide-x divide-slate-200 border-y border-slate-200">
              {[[`${CALCULATORS.length}+`, 'Calculators', ShieldCheck], [TRADES.length, 'Trades', Hammer], ['AI', 'Quoting', Bot]].map(([n, l, Icon]) => (
                <div key={l} className="py-4 px-4 first:pl-0" data-testid={`hero-stat-${l.toLowerCase()}`}>
                  <p className="font-display text-2xl font-medium text-slate-900">{n}</p>
                  <p className="mt-1 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-500"><Icon size={12} /> {l}</p>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <TrustpilotWidget className="max-w-md" />
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-5 tm-rise" style={{ animationDelay: '150ms' }}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-[28px] border border-gold/40" />
              <img
                src="https://images.unsplash.com/photo-1679797850019-3d0d8659a695?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000"
                alt="Craftsman at work"
                className="relative w-full h-[520px] object-cover rounded-3xl shadow-2xl"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-8 bg-white rounded-2xl shadow-xl border border-slate-200 px-5 py-4 w-64" data-testid="hero-quote-card">
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Quote accepted</p>
                <p className="font-display text-2xl text-slate-900 mt-1">£4,860.00</p>
                <p className="text-xs text-slate-500 mt-1">Bathroom refit · inc. VAT</p>
              </div>
            </div>
          </div>
        </div>

        {/* MARQUEE */}
        <div className="bg-slate-900 text-white overflow-hidden py-3.5">
          <div className="tm-marquee">
            {[...PILLS, ...PILLS].map((p, i) => (
              <span key={i} className="text-xs uppercase tracking-[0.25em] text-slate-300">
                <span className="text-gold mr-3">✦</span>{p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="tm-card p-8">
            <Calculator size={28} strokeWidth={1.5} className="text-brand" />
            <h3 className="font-display font-medium text-2xl mt-4">Every calculation, one place</h3>
            <p className="text-sm text-slate-600 mt-2">Bricks per m², concrete volume, copper pipe, tile adhesive, voltage drop, BTU sizing — instant results.</p>
          </div>
          <div className="tm-card p-8">
            <FileText size={28} strokeWidth={1.5} className="text-brand" />
            <h3 className="font-display font-medium text-2xl mt-4">Quote builder</h3>
            <p className="text-sm text-slate-600 mt-2">Add labour hours, day rate, materials, VAT 20% and your margin. Save and revisit any quote.</p>
          </div>
          <div className="tm-card p-8">
            <Bot size={28} strokeWidth={1.5} className="text-brand" />
            <h3 className="font-display font-medium text-2xl mt-4">AI estimator</h3>
            <p className="text-sm text-slate-600 mt-2">“Estimate a 4×3m bathroom refit.” Get materials, labour breakdown and total in plain English.</p>
          </div>
        </div>

        <div className="mt-6 tm-card p-5 flex items-center gap-3" style={{ background: 'rgb(var(--tm-brand-rgb) / 0.06)' }}>
          <span className="tm-chip tm-chip-orange shrink-0">NEW</span>
          <p className="text-sm sm:text-base">
            <span className="font-display font-bold uppercase">Always growing.</span>{' '}
            <span className="text-slate-700">New calculators, trades and material updates added every month — Pro members get them all included at no extra cost.</span>
          </p>
        </div>
      </section>

      {/* TRADES */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tight">Pick your trade</h2>
            <Link to="/trades" data-testid="home-view-all-trades" className="text-xs uppercase tracking-widest underline underline-offset-4 hover:text-brand font-semibold">View all →</Link>
          </div>
          <div data-testid={HOME.tradesGrid} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {TRADES.map((t) => {
              const count = CALCULATORS.filter((c) => c.trade === t.slug).length;
              return (
                <Link
                  key={t.slug}
                  to={`/trades/${t.slug}`}
                  data-testid={`trade-card-${t.slug}`}
                  className="tm-card tm-card-interactive p-5 group flex flex-col gap-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="tm-chip">{count} calc{count !== 1 ? 's' : ''}</span>
                    <ArrowRight size={18} className="text-slate-400 group-hover:text-brand transition-colors" />
                  </div>
                  <h3 className="font-display font-bold text-base sm:text-lg uppercase tracking-tight leading-tight mt-2">{t.name}</h3>
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{t.tagline}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tight mb-8">Frequently asked questions</h2>
          <div className="space-y-4" data-testid="landing-faq">
            {LANDING_FAQ.map((f, i) => (
              <div key={i} className="tm-card p-5" data-testid={`landing-faq-${i}`}>
                <h3 className="font-display font-bold text-lg uppercase tracking-tight">{f.q}</h3>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="tm-card tm-card-dark p-10 sm:p-14 rounded-3xl">
          <div className="grid md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tight">
                Stop guessing. <span className="text-brand">Start quoting</span>.
              </h2>
              <p className="text-slate-300 mt-3 max-w-xl">14-day free trial. £4.99/month after — cancel any time. Save unlimited quotes, use the AI estimator, calculate overheads and send branded PDF quotes.</p>
            </div>
            <div className="flex md:justify-end">
              <Link to="/pricing" data-testid="cta-bottom-start" className="tm-btn">
                Start 14-Day Free Trial
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
